import { useCallback, useMemo, useRef, useState } from 'react';

import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import SnapshotDialog from '@webclient/components/search-forms/gamification-search/actions/file-attachment/sections/take-a-photo/snaphsot-dialog';
import {
  StyledImage,
  StyledKeepMountedVideo,
} from '@webclient/components/search-forms/gamification-search/actions/file-attachment/sections/take-a-photo/styles';
import useSharedUppyInstance from '@webclient/components/search-forms/gamification-search/hooks/useSharedUppyInstance';

import CameraRoundedIcon from '~icons/knowz-iconify/camera-rounded';
import CheckRoundedIcon from '~icons/knowz-iconify/check-rounded';
import DeleteRoundedIcon from '~icons/knowz-iconify/delete-rounded';

import { IconButton, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import { Body, Meta } from '@uppy/core';
import Webcam from '@uppy/webcam';

export default function TakeAPhoto() {
  const [preview, setPreview] = useState<{
    file: File;
    src: string;
    name: string;
  } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslate();
  const { uppy } = useSharedUppyInstance();

  async function handleGetCamera() {
    const stream = await navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .catch(function (error) {
        console.error('Microphone access error:', error);
      });
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play();
      };
      setIsDialogOpen(true);
    }
  }

  const takeSnapshot = useCallback(async () => {
    const webcam = uppy.getPlugin('Webcam') as Webcam<Meta, Body> | undefined;
    // await webcam?.oneTwoThreeSmile(); we can add this later if needed
    try {
      if (!webcam) return;
      webcam.render();
      //? monkey patching the webcam plugin to get the video element
      webcam.getVideoElement = () => videoRef.current;

      const { data: snapshotBlob, name } = await webcam.getImage();
      if (!snapshotBlob) return;

      const src = URL.createObjectURL(snapshotBlob as Blob);

      setPreview({
        src,
        file: snapshotBlob as File,
        name,
      });
    } catch (error) {
      console.error(error);
    }
  }, [uppy]);

  const deleteSnapshot = useCallback(() => {
    setPreview(null);
  }, []);

  const shouldRenderPreview = !!preview;

  const handleCloseSnapshotDialog = useCallback(async () => {
    deleteSnapshot();
    setIsDialogOpen(false);

    if (videoRef.current) {
      const stream = videoRef.current.srcObject as MediaStream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => {
          track.stop();
        });
      }
      videoRef.current.srcObject = null;
      videoRef.current.src = '';
    }

    const webcam = uppy.getPlugin('Webcam') as Webcam<Meta, Body> | undefined;
    if (webcam) {
      await webcam.stop().catch((error) => console.error(error));
    }
  }, [deleteSnapshot, uppy]);

  const uploadSnapshot = useCallback(async () => {
    if (!preview) {
      throw new Error('No preview to upload');
    }
    const { file, name } = preview;
    uppy.addFile({
      data: file,
      name,
    });
    handleCloseSnapshotDialog();
  }, [handleCloseSnapshotDialog, preview, uppy]);

  const PreviewActionButtons = useMemo(
    () => [
      <IconButton
        variant="contained"
        color="primary"
        shape="fullRounded"
        onClick={uploadSnapshot}
      >
        <CheckRoundedIcon />
      </IconButton>,
      <IconButton
        variant="outlined"
        color="error"
        shape="fullRounded"
        onClick={deleteSnapshot}
      >
        <DeleteRoundedIcon />
      </IconButton>,
    ],
    [deleteSnapshot, uploadSnapshot],
  );

  const SnapshotActionButtons = useMemo(
    () => [
      <IconButton
        variant="outlined"
        color="primary"
        shape="fullRounded"
        onClick={takeSnapshot}
      >
        <CameraRoundedIcon />
      </IconButton>,
    ],
    [takeSnapshot],
  );

  return (
    <>
      <SnapshotDialog
        open={isDialogOpen}
        onClose={handleCloseSnapshotDialog}
        ActionButtons={
          shouldRenderPreview ? PreviewActionButtons : SnapshotActionButtons
        }
        deleteSnapshot={deleteSnapshot}
      >
        <>
          {preview && (
            <StyledImage
              src={preview.src}
              alt="preview"
            />
          )}
          <StyledKeepMountedVideo
            show={shouldRenderPreview}
            ref={videoRef}
            preload="auto"
            autoPlay
            playsInline
          />
        </>
      </SnapshotDialog>
      <ListItemButton
        disableGutters
        disableRipple
        disableTouchRipple
        onClick={handleGetCamera}
      >
        <ListItemIcon>
          <CameraRoundedIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <Text
              variant="textSm"
              weight="semibold"
            >
              {t('layout.searchWidget.attachments.takeAPhoto', 'Take a photo')}
            </Text>
          }
        />
      </ListItemButton>
    </>
  );
}
